import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tag, MapPin, Gem, ShieldCheck, Plus, Trash2, Pencil, X, Check, Loader2 } from 'lucide-react';
import { categoriesAPI } from '../utils/api';

const TABS = [
  { key: 'conditions', label: 'Conditions', icon: ShieldCheck },
  { key: 'materials', label: 'Materials', icon: Gem },
  { key: 'locations', label: 'Locations', icon: MapPin },
  { key: 'brands', label: 'Brands', icon: Tag },
];

const Categories = () => {
  const [apiData, setApiData] = useState({ brands: [], conditions: [], materials: [], locations: [] });
  const [localCategories, setLocalCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('conditions');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [raw, local] = await Promise.all([
        categoriesAPI.getRaw(),
        categoriesAPI.getLocal(),
      ]);
      if (raw) setApiData(raw);
      if (Array.isArray(local)) setLocalCategories(local);
    } catch (e) { console.error('Load failed:', e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Merge auto-extracted + manually added for active tab
  const autoItems = (apiData[activeTab] || []).map(name => ({
    id: `auto-${name}`, name, slug: name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
    source: 'auto', description: '',
  }));
  const manualItems = localCategories
    .filter(c => c.type === activeTab)
    .map(c => ({ ...c, source: 'manual' }));

  // Deduplicate: manual overrides auto if same slug
  const autoSlugs = new Set(autoItems.map(i => i.slug));
  const manualSlugs = new Set(manualItems.map(i => i.slug));
  const uniqueAuto = autoItems.filter(i => !manualSlugs.has(i.slug));
  const mergedItems = [...manualItems, ...uniqueAuto];

  const tabCounts = TABS.reduce((acc, t) => {
    const auto = (apiData[t.key] || []).length;
    const manual = localCategories.filter(c => c.type === t.key).length;
    const autoS = new Set((apiData[t.key] || []).map(n => n.toLowerCase().replace(/[^a-z0-9]+/g, '-')));
    const manualS = new Set(localCategories.filter(c => c.type === t.key).map(c => c.slug));
    const uniqueAutoCount = [...autoS].filter(s => !manualS.has(s)).length;
    acc[t.key] = uniqueAutoCount + manual;
    return acc;
  }, {});

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await categoriesAPI.createLocal({ type: activeTab, name: newName.trim(), description: newDesc.trim() });
      setNewName('');
      setNewDesc('');
      setShowAddForm(false);
      await loadData();
    } catch (e) { alert(e.message || 'Failed to add category'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await categoriesAPI.deleteLocal(id);
      await loadData();
    } catch (e) { alert(e.message || 'Failed to delete'); }
  };

  const handleEditSave = async (id) => {
    if (!editName.trim()) return;
    try {
      await categoriesAPI.updateLocal(id, { name: editName.trim() });
      setEditId(null);
      await loadData();
    } catch (e) { alert(e.message || 'Failed to update'); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="text-muted-foreground animate-pulse">Loading...</div></div>;

  const totalAll = Object.values(tabCounts).reduce((s, n) => s + n, 0);

  return (
    <div className="space-y-6" data-testid="categories-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Categories</h1>
          <p className="text-muted-foreground mt-1 text-sm">{totalAll} total across all types</p>
        </div>
        <Button data-testid="add-category-btn" onClick={() => setShowAddForm(true)} size="sm" className="bg-[#C9A96E] hover:bg-[#b8954f] text-black">
          <Plus className="h-4 w-4 mr-1.5" /> Add Category
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {TABS.map(t => (
          <Button key={t.key} variant={activeTab === t.key ? 'default' : 'outline'} size="sm"
            data-testid={`tab-${t.key}`}
            onClick={() => { setActiveTab(t.key); setShowAddForm(false); setEditId(null); }}
            className={activeTab === t.key ? 'bg-primary text-primary-foreground' : ''}>
            <t.icon className="h-3.5 w-3.5 mr-1.5" />
            {t.label}
            <Badge variant="outline" className="ml-2 text-[10px] h-4 px-1">{tabCounts[t.key] || 0}</Badge>
          </Button>
        ))}
      </div>

      {/* Add Form */}
      {showAddForm && (
        <Card className="p-4 border-[#C9A96E]/30" data-testid="add-category-form">
          <h3 className="text-sm font-semibold mb-3 text-[#C9A96E]">
            Add to {TABS.find(t => t.key === activeTab)?.label}
          </h3>
          <div className="flex gap-3 items-end flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs text-muted-foreground mb-1 block">Name *</label>
              <Input data-testid="category-name-input" value={newName} onChange={e => setNewName(e.target.value)}
                placeholder={`e.g. ${activeTab === 'conditions' ? 'Pre-Owned' : activeTab === 'materials' ? 'Titanium' : activeTab === 'locations' ? 'Tokyo, Japan' : 'Tudor'}`}
                onKeyDown={e => e.key === 'Enter' && handleAdd()} />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs text-muted-foreground mb-1 block">Description</label>
              <Input data-testid="category-desc-input" value={newDesc} onChange={e => setNewDesc(e.target.value)}
                placeholder="Optional description" onKeyDown={e => e.key === 'Enter' && handleAdd()} />
            </div>
            <div className="flex gap-2">
              <Button data-testid="save-category-btn" onClick={handleAdd} disabled={saving || !newName.trim()} size="sm"
                className="bg-[#C9A96E] hover:bg-[#b8954f] text-black">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                <span className="ml-1">Save</span>
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setShowAddForm(false); setNewName(''); setNewDesc(''); }}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Table */}
      <Card className="overflow-hidden">
        <Table>
          <TableHeader className="bg-muted/50">
            <TableRow>
              <TableHead className="w-12">#</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Source</TableHead>
              <TableHead className="w-24 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {mergedItems.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                No {activeTab} found. Click "Add Category" to create one.
              </TableCell></TableRow>
            ) : mergedItems.map((item, i) => (
              <TableRow key={item.id} data-testid={`category-row-${item.id}`}>
                <TableCell className="text-muted-foreground text-xs">{i + 1}</TableCell>
                <TableCell>
                  {editId === item.id ? (
                    <div className="flex gap-2 items-center">
                      <Input value={editName} onChange={e => setEditName(e.target.value)}
                        className="h-7 text-sm" onKeyDown={e => e.key === 'Enter' && handleEditSave(item.id)} />
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleEditSave(item.id)}>
                        <Check className="h-3.5 w-3.5 text-green-500" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditId(null)}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <span className="font-medium">{item.name}</span>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="font-mono text-xs">{item.slug}</Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={item.source === 'manual' ? 'default' : 'secondary'} className="text-[10px]">
                    {item.source === 'manual' ? 'Manual' : 'Auto'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {item.source === 'manual' && editId !== item.id && (
                    <div className="flex gap-1 justify-end">
                      <Button data-testid={`edit-${item.id}`} size="icon" variant="ghost" className="h-7 w-7"
                        onClick={() => { setEditId(item.id); setEditName(item.name); }}>
                        <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                      </Button>
                      <Button data-testid={`delete-${item.id}`} size="icon" variant="ghost" className="h-7 w-7"
                        onClick={() => handleDelete(item.id)}>
                        <Trash2 className="h-3.5 w-3.5 text-red-400" />
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <p className="text-xs text-muted-foreground">
        <strong>Auto</strong> categories are extracted from your watch listings. <strong>Manual</strong> categories are added by you and can be edited or deleted.
      </p>
    </div>
  );
};

export default Categories;
